export type SupportLocale = "ko" | "en";

export interface SupportCopy {
  languageToggleLabel: string;
  eyebrow: string;
  title: string;
  description: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  subjectLabel: string;
  subjectPlaceholder: string;
  bodyLabel: string;
  bodyPlaceholder: string;
  submit: string;
  submitting: string;
  submitAnother: string;
  successTitle: string;
  successBody: (reference: string) => string;
  ambiguousTitle: string;
  ambiguousBody: string;
  rejectedTitle: string;
  fallbackErrorBody: string;
  validation: {
    nameRequired: string;
    nameMax: string;
    emailInvalid: string;
    emailMax: string;
    subjectRequired: string;
    subjectMax: string;
    bodyRequired: string;
    bodyMax: string;
  };
}

// Mirrors the server's real validation exactly (apps/server/src/modules/support/dto/support.dto.ts
// CreateSupportTicketDto: name Length(1,100), email MaxLength(254), subject Length(1,200), body Length(1,10_000)),
// re-read at this pass — previously this file under-capped subject (150) and body (4000) below what the
// server actually accepts, which would have rejected valid customer input the server would have taken.
export const SUPPORT_FORM_LIMITS = {
  name: 100,
  email: 254,
  subject: 200,
  body: 10_000,
} as const;

export const supportCopy: Record<SupportLocale, SupportCopy> = {
  ko: {
    languageToggleLabel: "English",
    eyebrow: "고객 지원",
    title: "무엇을 도와드릴까요?",
    // NOTE: No specific response-time SLA is approved yet — do not add one back
    // without an explicit, approved commitment (see session correction).
    description: "궁금하신 점이나 문제를 남겨주시면 BackPac 팀이 확인 후 이메일로 답변드립니다.",
    nameLabel: "이름",
    namePlaceholder: "성함을 입력해주세요",
    emailLabel: "이메일",
    emailPlaceholder: "답변받으실 이메일 주소",
    subjectLabel: "제목",
    subjectPlaceholder: "문의 제목을 간단히 적어주세요",
    bodyLabel: "내용",
    bodyPlaceholder: "문의하실 내용을 자세히 작성해주세요",
    submit: "문의 보내기",
    submitting: "보내는 중...",
    submitAnother: "다른 문의 보내기",
    successTitle: "문의가 접수되었습니다",
    successBody: (reference) => `접수 번호는 ${reference} 입니다. 회신은 입력하신 이메일로 보내드립니다.`,
    ambiguousTitle: "전송 확인이 지연되고 있어요",
    ambiguousBody:
      "네트워크 문제로 접수 여부를 바로 확인하지 못했습니다. 같은 내용으로 다시 보내셔도 중복 접수되지 않으니 안심하고 다시 시도해주세요.",
    rejectedTitle: "문의를 접수하지 못했습니다",
    fallbackErrorBody: "일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
    validation: {
      nameRequired: "이름을 입력해주세요.",
      nameMax: `이름은 ${SUPPORT_FORM_LIMITS.name}자 이내로 입력해주세요.`,
      emailInvalid: "올바른 이메일 주소를 입력해주세요.",
      emailMax: `이메일은 ${SUPPORT_FORM_LIMITS.email}자 이내로 입력해주세요.`,
      subjectRequired: "제목을 입력해주세요.",
      subjectMax: `제목은 ${SUPPORT_FORM_LIMITS.subject}자 이내로 입력해주세요.`,
      bodyRequired: "내용을 입력해주세요.",
      bodyMax: `내용은 ${SUPPORT_FORM_LIMITS.body}자 이내로 입력해주세요.`,
    },
  },
  en: {
    languageToggleLabel: "한국어",
    eyebrow: "Customer support",
    title: "How can we help?",
    // NOTE: No specific response-time SLA is approved yet — do not add one back
    // without an explicit, approved commitment (see session correction).
    description: "Leave your question or issue below and the BackPac team will reply by email.",
    nameLabel: "Name",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "The email address we should reply to",
    subjectLabel: "Subject",
    subjectPlaceholder: "A short summary of your question",
    bodyLabel: "Message",
    bodyPlaceholder: "Please describe your question or issue in detail",
    submit: "Send message",
    submitting: "Sending...",
    submitAnother: "Send another message",
    successTitle: "Your message has been received",
    successBody: (reference) => `Your reference number is ${reference}. We'll reply to the email address you provided.`,
    ambiguousTitle: "We couldn't confirm delivery yet",
    ambiguousBody:
      "A network issue kept us from confirming your message went through. It's safe to submit again with the same details — we won't create a duplicate ticket.",
    rejectedTitle: "We couldn't submit your message",
    fallbackErrorBody: "Something went wrong. Please try again in a moment.",
    validation: {
      nameRequired: "Please enter your name.",
      nameMax: `Name must be ${SUPPORT_FORM_LIMITS.name} characters or fewer.`,
      emailInvalid: "Please enter a valid email address.",
      emailMax: `Email must be ${SUPPORT_FORM_LIMITS.email} characters or fewer.`,
      subjectRequired: "Please enter a subject.",
      subjectMax: `Subject must be ${SUPPORT_FORM_LIMITS.subject} characters or fewer.`,
      bodyRequired: "Please enter a message.",
      bodyMax: `Message must be ${SUPPORT_FORM_LIMITS.body} characters or fewer.`,
    },
  },
};
